import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { IHomeTileConfigUpdateBody, IHomeTileOption } from "@/api_framework/api_modals/HomeTileConfig";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useCouponUserPerSonaListAPI } from "@/app/hooks/api_hooks/coupon/useCouponUserPerSonaListAPI";
import { useHomeTileConfigAPI } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileConfigAPI";
import { useHomeTileListAPI } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileListAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { BackLink, TileAvatar } from "../components";
import { cardClassName, IHomeTileConfigFormFields, TILE_TYPE_LABEL } from "../interface";
import { validatePersonaCombination } from "../personaExpression";
import { DEFAULT_VALUES, buildChanges, buildSaveBody, hasStaticRibbon, toFormValues } from "./formUtils";
import { PreviewCard } from "./PreviewCard";
import { RibbonRuleCard } from "./RibbonRuleCard";
import { RibbonStyleCard } from "./RibbonStyleCard";

const TileHero = ({ tile, isEditMode }: { tile?: IHomeTileOption; isEditMode: boolean }) => (
  <div className="relative overflow-hidden rounded-xl border border-[#193A6A]/15 bg-gradient-to-r from-[#EEF5FF] via-white to-white shadow-sm">
    <span className="absolute inset-y-0 left-0 w-1 bg-[#193A6A]" aria-hidden="true" />
    <div className="flex flex-wrap items-center gap-4 py-4 pl-6 pr-5">
      <TileAvatar
        name={tile?.tile_name ?? "?"}
        imageUrl={tile?.image_url}
        className="h-16 w-16 rounded-xl bg-white p-2.5 text-lg shadow-sm ring-1 ring-[#193A6A]/15"
      />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#2B5DA8]">
          {isEditMode ? "Edit Rule" : "Add Rule"}
        </div>
        <h1 className="truncate text-lg font-semibold leading-7 text-gray-900">{tile?.tile_name ?? "Home Tile"}</h1>
        <p className="text-xs text-gray-500">Controls the tile ribbon only, not the discount.</p>
      </div>
      {tile && (
        <span className="rounded-full border border-[#193A6A]/20 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#193A6A]">
          {TILE_TYPE_LABEL[tile.tile_type]}
        </span>
      )}
    </div>
  </div>
);

const FormActions = ({ isSaving, onCancel }: { isSaving: boolean; onCancel: () => void }) => (
  <div className="sticky bottom-0 z-10 mt-4 flex items-center justify-end gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
    <button
      type="button"
      className="rounded-lg border border-slate-200 bg-white px-8 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
      onClick={onCancel}
      disabled={isSaving}
    >
      Back
    </button>
    <button
      type="submit"
      className="rounded-lg bg-[#193A6A] px-8 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#132c52] disabled:cursor-not-allowed disabled:opacity-60"
      disabled={isSaving}
    >
      {isSaving ? "Saving…" : "Save Configuration"}
    </button>
  </div>
);

const HomeTileOnboard = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const configId = params.get("config_id");
  const tileIdParam = params.get("tile_id") ?? "";
  const isEditMode = Boolean(configId);

  const { execute: fetchUserPersonas, userpersona, isLoading: isPersonaListLoading } = useCouponUserPerSonaListAPI();
  const { configList, isListLoading, isSaving, fetchTileConfigs, saveConfig } = useHomeTileConfigAPI();
  const { tileList, isLoading: isTileListLoading, execute: fetchTiles } = useHomeTileListAPI();

  const [isMissingConfig, setIsMissingConfig] = useState<boolean>(false);

  const formHook = useForm<IHomeTileConfigFormFields>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: { ...DEFAULT_VALUES, tile_id: tileIdParam },
  });
  const { control, getValues, handleSubmit, reset, trigger } = formHook;

  const selectedTileId = useWatch({ control, name: "tile_id" });
  const tile = useMemo(() => tileList.find((option) => option.tile_id === selectedTileId), [tileList, selectedTileId]);

  useEffect(() => {
    fetchUserPersonas();
  }, [fetchUserPersonas]);

  useEffect(() => {
    fetchTiles();
  }, [fetchTiles]);

  useEffect(() => {
    if (!tileIdParam) return;
    let isCurrent = true;
    fetchTileConfigs(tileIdParam).then((configs) => {
      if (!isCurrent || !configs || !configId) return;
      const config = configs.find((item) => item.id === configId);
      setIsMissingConfig(!config);
      if (!config) return;
      reset(toFormValues(config));
    });
    return () => {
      isCurrent = false;
    };
  }, [tileIdParam, configId, fetchTileConfigs, reset]);

  const otherConfigs = useMemo(
    () =>
      configList
        .filter((config) => config.id !== configId)
        .map((config) => ({
          id: config.id,
          persona_rule: config.persona_rule,
          text: config.ribbon_text,
          ribbon: config.ribbon,
          status: config.status,
        })),
    [configList, configId],
  );

  const validatePersonaField = useCallback(
    (value: string): true | string => {
      if (!hasStaticRibbon(getValues())) return true;
      if (isPersonaListLoading) return "Personas are still loading.";
      if (!userpersona.length) return "Couldn't load the persona list, so the combination can't be checked.";
      if (!value.trim() && getValues("persona_logic_mode") === "BASIC") {
        return "Select at least one persona to include or exclude.";
      }

      const parsed = validatePersonaCombination(value, userpersona);
      return parsed.isValid || (parsed.error as string);
    },
    [getValues, isPersonaListLoading, userpersona],
  );

  useEffect(() => {
    if (isPersonaListLoading || !getValues("persona_combination")) return;
    void trigger("persona_combination");
  }, [isPersonaListLoading, userpersona, trigger, getValues]);

  const tileUrl = `${getRouteKey("HOME_TILE_SETUP", "url")}${tileIdParam ? `?tile_id=${tileIdParam}` : ""}`;

  const handleCancel = () => {
    reset(DEFAULT_VALUES);
    navigate(tileUrl);
  };

  const onSubmit = async (data: IHomeTileConfigFormFields) => {
    if (!tile?.tile_type) {
      toast.error("This configuration has no tile attached. Start again from the tile list.");
      return;
    }
    if (!hasStaticRibbon(data)) {
      toast.error("Add a persona combination and ribbon text.");
      return;
    }

    const parsedPersona = validatePersonaCombination(data.persona_combination, userpersona);
    if (!parsedPersona.rule) {
      toast.error(parsedPersona.error || "Enter a valid persona combination.");
      return;
    }

    const body = buildSaveBody(data, parsedPersona.rule, tile.tile_type === "SERVICE");

    if (!configId) {
      if (await saveConfig(body)) navigate(tileUrl);
      return;
    }

    const savedConfig = configList.find((config) => config.id === configId);
    const changes: IHomeTileConfigUpdateBody = savedConfig ? buildChanges(body, savedConfig) : body;

    if (!Object.keys(changes).length) {
      toast.success("No changes to save.");
      navigate(tileUrl);
      return;
    }

    if (await saveConfig(changes, configId)) navigate(tileUrl);
  };

  const onInvalid = () => toast.error("Fix the highlighted fields before saving.");

  if (isEditMode && (!tileIdParam || isMissingConfig)) {
    return (
      <div className="px-5 py-4">
        <BackLink label="Back to configurations" onClick={handleCancel} />
        <div className={`${cardClassName} px-5 py-10 text-center`}>
          <p className="text-sm font-semibold text-slate-700">This configuration could not be opened.</p>
          <p className="mt-1 text-xs text-slate-500">
            Open it from its tile on the configuration list so the tile is carried along.
          </p>
        </div>
      </div>
    );
  }

  if (isTileListLoading || (isEditMode && isListLoading)) {
    return (
      <div className="bg-white px-5 py-4">
        <SkeletonType1 />
        <SkeletonType1 className="mt-8" />
      </div>
    );
  }

  return (
    <div className="px-5 py-4">
      <FormProvider {...formHook}>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <BackLink label="Back to configurations" onClick={handleCancel} />

          <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
            <div className="min-w-0 flex-1 space-y-4">
              <TileHero tile={tile} isEditMode={isEditMode} />
              <RibbonRuleCard
                personas={userpersona}
                isPersonaListLoading={isPersonaListLoading}
                validatePersona={validatePersonaField}
                existingConfigs={otherConfigs}
                isServiceTile={tile?.tile_type === "SERVICE"}
              />
              <RibbonStyleCard />
            </div>
            <PreviewCard tile={tile} />
          </div>

          <FormActions isSaving={isSaving} onCancel={handleCancel} />
        </form>
      </FormProvider>
    </div>
  );
};

export default HomeTileOnboard;
